import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Bubble } from 'tea-component';
import TagItem from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/TagItem';

import '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/GameInfo/components/TagList/index.scss';

export interface IFilterTagItem {
  clickValue: string;
  displayText: string;
}

interface IFilterTagCellListProps {
  tags: IFilterTagItem[];
  filterKey: string;
  onTagClick?: (filterKey: string, clickValue: string) => void;
}

const FilterTagCellList: React.FC<IFilterTagCellListProps> = ({ tags, filterKey, onTagClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleTags, setVisibleTags] = useState(tags);
  const [overflowCount, setOverflowCount] = useState(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const children = Array.from(container.children);
    const maxHeight = container.offsetHeight;
    let visibleCount = 0;

    for (const child of children) {
      const bottom = child.getBoundingClientRect().bottom - container.getBoundingClientRect().top;
      if (bottom > maxHeight) {
        break;
      }
      visibleCount += 1;
    }

    const tagsToShow = Math.min(visibleCount, tags.length);
    setVisibleTags(tags.slice(0, tagsToShow));
    setOverflowCount(Math.max(tags.length - tagsToShow, 0));
  }, [tags]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const observer = new MutationObserver(() => {
      const children = Array.from(container.children);
      const lastChild = children[children.length - 1];
      const bottom = lastChild.getBoundingClientRect().bottom - container.getBoundingClientRect().top;

      if (bottom > container.offsetHeight) {
        setVisibleTags((prev) => prev.slice(0, -1));
        setOverflowCount((count) => count + 1);
      }
    });

    observer.observe(container, { childList: true });
    return () => observer.disconnect();
  }, []);

  const hiddenTags = useMemo(() => tags.slice(-overflowCount), [tags, overflowCount]);
  const handleClick = (clickValue: string) => onTagClick?.(filterKey, clickValue);

  if (!tags.length) {
    return null;
  }

  return (
    <div className="dbi_tag_list_container" ref={containerRef}>
      {visibleTags.map((tag) => (
        <TagItem
          key={tag.clickValue}
          text={tag.displayText}
          onClick={() => handleClick(tag.clickValue)}
        />
      ))}
      {overflowCount > 0 && (
        <Bubble
          content={
            <div className="dbi_tag_list_container_more">
              {hiddenTags.map((tag) => (
                <TagItem
                  key={tag.clickValue}
                  text={tag.displayText}
                  onClick={() => handleClick(tag.clickValue)}
                />
              ))}
            </div>
          }
        >
          <div>
            <TagItem>+{overflowCount}</TagItem>
          </div>
        </Bubble>
      )}
    </div>
  );
};

export default FilterTagCellList;
